import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, Send, Paperclip, Calendar, Download } from "lucide-react";

export function FounderConciergeChat({ api, founder }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/director/concierge");
      setMessages(data.messages || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("body", text.trim() || "(attachment)");
      if (file) fd.append("attachment", file);
      await api.post("/director/concierge", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText(""); setFile(null);
      toast.success("Sent to the Board Secretariat.");
      load();
    } catch { toast.error("Could not send message."); }
    finally { setBusy(false); }
  };

  const downloadAttachment = async (m) => {
    try {
      const r = await api.get(`/director/concierge/attachment/${m.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = m.attachment_filename || "vinci-concierge";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download attachment."); }
  };

  return (
    <div className="rounded-sm border border-[#C9A961]/25 bg-gradient-to-br from-[#0f0d0a] to-[#0a0806] p-8 mb-16" data-testid="director-concierge">
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#C9A961]">· Founder Concierge · Direct Line to the Board Secretariat ·</span>
          <h2 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-2">Private Inbox</h2>
        </div>
        <MessageCircle className="text-[#C9A961]" size={26} />
      </div>

      <div ref={scrollRef} className="max-h-[420px] overflow-y-auto space-y-3 pr-2 mb-4">
        {loading && <p className="font-body text-[#8A8A82]">Loading…</p>}
        {!loading && messages.length === 0 && (
          <p className="font-body text-[#8A8A82] italic text-center py-8">
            No messages yet. Say hello to the Vinci Board.
          </p>
        )}
        {messages.map((m) => {
          const isAdmin = m.from_role === "admin";
          return (
            <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              className={`flex ${isAdmin ? "justify-start" : "justify-end"}`} data-testid={`concierge-msg-${m.id}`}>
              <div className={`max-w-[75%] rounded-sm p-4 ${
                isAdmin ? "border border-[#C9A961]/40 bg-[#C9A961]/[0.06]" : "border border-white/10 bg-black/40"
              }`}>
                <div className={`font-grotesk text-[9px] uppercase tracking-[0.25em] mb-1 ${
                  isAdmin ? "text-[#C9A961]" : "text-[#8A8A82]"
                }`}>
                  {isAdmin ? "· Board Secretariat ·" : (m.author_name || "You")}
                </div>
                <p className="font-body text-sm text-[#F5F5F0] whitespace-pre-wrap">{m.body}</p>
                {m.meeting_at && (
                  <div className="mt-3 flex items-center gap-2 rounded-sm border border-[#C9A961]/40 bg-black/40 px-3 py-2">
                    <Calendar size={14} className="text-[#C9A961]" />
                    <div className="flex-1">
                      <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9A961]">Meeting</div>
                      <div className="font-body text-xs text-[#F5F5F0]">{m.meeting_at}</div>
                    </div>
                    {m.meeting_link && (
                      <a href={m.meeting_link} target="_blank" rel="noopener noreferrer"
                        className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9A961] hover:underline">
                        Join →
                      </a>
                    )}
                  </div>
                )}
                {m.attachment_filename && (
                  <button onClick={() => downloadAttachment(m)}
                    className="mt-3 inline-flex items-center gap-2 rounded-sm border border-[#C9A961]/40 px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#C9A961] hover:bg-[#C9A961] hover:text-black transition-colors">
                    <Download size={12} /> {m.attachment_filename}
                  </button>
                )}
                <div className="font-body text-[10px] text-[#8A8A82] mt-2">
                  {new Date(m.created_at).toLocaleString()}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <form onSubmit={send} className="flex flex-wrap items-end gap-3 pt-4 border-t border-[#C9A961]/15">
        <label className="cursor-pointer inline-flex items-center gap-2 rounded-sm border border-white/15 px-3 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9C9C2] hover:border-[#C9A961]">
          <Paperclip size={13} /> {file ? file.name.slice(0, 24) : "Attach"}
          <input type="file" hidden onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="concierge-attach" />
        </label>
        <textarea value={text} onChange={(e) => setText(e.target.value)}
          data-testid="concierge-input"
          placeholder="Send a note to the Board Secretariat…" rows={2}
          className="flex-1 min-w-[240px] bg-transparent border border-white/15 focus:border-[#C9A961] rounded-sm p-3 font-body text-sm text-[#F5F5F0] outline-none" />
        <button type="submit" disabled={busy} data-testid="concierge-send"
          className="rounded-sm bg-[#C9A961] text-black px-5 py-3 font-grotesk text-[10px] uppercase tracking-[0.24em] hover:bg-[#8B7333] hover:text-[#F5F5F0] transition-colors disabled:opacity-60 inline-flex items-center gap-2">
          <Send size={14} /> Send
        </button>
      </form>
    </div>
  );
}

/* ─── USD Toggle hook ──────────────────────────────────────── */
export function useCurrencyToggle(api) {
  const [currency, setCurrency] = useState(() => localStorage.getItem("vinci_founder_ccy") || "INR");
  const [fx, setFx] = useState({ inr_per_usd: 83.0, as_of: "" });
  useEffect(() => {
    api.get("/director/fx").then((r) => setFx(r.data)).catch(() => {});
  }, [api]);
  const toggle = () => {
    const next = currency === "INR" ? "USD" : "INR";
    setCurrency(next); localStorage.setItem("vinci_founder_ccy", next);
  };
  const fmt = (v) => {
    const n = parseFloat(v) || 0;
    if (currency === "USD") {
      const usd = n / (fx.inr_per_usd || 83);
      if (usd >= 1e6) return `$${(usd/1e6).toFixed(2)}M`;
      if (usd >= 1e3) return `$${(usd/1e3).toFixed(2)}K`;
      return `$${usd.toFixed(0)}`;
    }
    if (n >= 1e7) return `₹${(n/1e7).toFixed(2)} Cr`;
    if (n >= 1e5) return `₹${(n/1e5).toFixed(2)} L`;
    return `₹${Math.round(n).toLocaleString()}`;
  };
  return { currency, setCurrency, toggle, fx, fmt };
}

export function CurrencyToggle({ currency, toggle, fx }) {
  return (
    <button onClick={toggle} data-testid="director-currency-toggle"
      className="inline-flex items-center gap-2 rounded-full border border-[#C9A961]/50 text-[#C9A961] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.28em] hover:bg-[#C9A961] hover:text-black transition-colors"
      title={`1 USD = ₹${(fx.inr_per_usd || 83).toFixed(2)} · ${fx.as_of || ""}`}>
      <span className={currency === "INR" ? "opacity-100" : "opacity-40"}>₹ INR</span>
      <span className="text-[#8A8A82]">·</span>
      <span className={currency === "USD" ? "opacity-100" : "opacity-40"}>$ USD</span>
    </button>
  );
}
